/**
 * ========================================
 * Entity 관련 doc_variables 생성 유틸리티
 * ========================================
 * Entity Doc Variables Utility Functions
 * - Entity 정보를 조회하여 공통 doc_variables 생성
 * - L_ENTITY 테이블 조회 및 SSIC 코드 조회 포함
 */

import { ref } from 'vue';
import { entityApi } from '@/features/Entity/api/entityApi';
import { useAddressFormatter } from './useAddressFormatter';
import { formatDocDate } from './dateUtils';
import { useSessionStore } from '@/shared/stores/sessionStore';
import { baseCodeApi } from '@/shared/api/baseCodeApi';

// SSIC 코드 캐시 (코드 -> SSIC_TITLE 매핑)
const ssicCache = ref<Map<string, string>>(new Map());
const isSSICLoaded = ref(false);

/**
 * SSIC 목록 로드 및 캐시
 */
const loadSSICCache = async (): Promise<void> => {
  if (isSSICLoaded.value) {
    return; // 이미 로드됨
  }

  try {
    const response = await entityApi.getSSICList();
    const codes = (response as any).data?.codes || response.data?.data?.codes || [];
    
    const cache = new Map<string, string>();
    codes.forEach((code: any) => {
      const ssicCd = String(code.TOTAL_CODE || code.totalCode || '').trim();
      const ssicTitle = String(code.CODE_NAME || code.codeName || '').trim();
      if (ssicCd && ssicTitle) {
        cache.set(ssicCd, ssicTitle);
      }
    });
    
    ssicCache.value = cache;
    isSSICLoaded.value = true;
    console.log('✅ [useEntityDocVariables] SSIC 캐시 로드 완료:', cache.size);
  } catch (error) {
    console.error('❌ [useEntityDocVariables] SSIC 캐시 로드 실패:', error);
    ssicCache.value = new Map();
  }
};

/**
 * SSIC 코드로 SSIC_TITLE 조회
 */
export const getSSICTitle = (ssicCd: string): string => {
  if (!ssicCd) return '';
  return ssicCache.value.get(ssicCd) || '';
};

/**
 * Entity 정보 인터페이스
 */
export interface EntityData {
  companyName?: string;
  registrationNumber?: string;
  incorporationDate?: string;
  companyAddress?: string;
  financialYearEnd?: string;
  primaryActivityCode?: string;
  primaryActivityDescription?: string;
  secondaryActivityCode?: string;
  secondaryActivityDescription?: string;
  entityJurisdiction?: string;
  entityType?: string;
  companyEmail?: string;
}

/**
 * Entity 정보를 조회하여 반환 (doc_variables 생성하지 않음)
 * @param entityCd - Entity CD (ENTITY_CD)
 * @returns Entity 데이터 객체
 */
export const useEntityDocVariables = () => {
  const { formatAddress } = useAddressFormatter();

  /**
   * Entity 데이터 조회 함수 (doc_variables 생성하지 않음)
   */
  const getEntityData = async (entityCd: string): Promise<EntityData> => {
    if (!entityCd) {
      return {};
    }

    try {
      // ⭐ SSIC 캐시와 Entity Profile을 병렬로 로드하여 성능 최적화
      const [_, profileResponse] = await Promise.all([
        loadSSICCache(),
        entityApi.getEntityProfile(entityCd)
      ]);
      
      const profileData = (profileResponse as any).data || profileResponse;

      if (!profileData) {
        console.warn('⚠️ [useEntityDocVariables] Entity Profile 데이터 없음:', entityCd);
        return {};
      }

      const entityData: EntityData = {};

      // COMPANY_NAME
      entityData.companyName = profileData.companyName || '';

      // REG_NO
      entityData.registrationNumber = profileData.registrySync?.registrationNumber || '';

      // DATE_OF_INCORPORATION (날짜 형식 그대로 사용)
      const incorporationDate = profileData.accountingDates?.incorporationDate || '';
      if (incorporationDate) {
        entityData.incorporationDate = incorporationDate; // 날짜 형식 그대로 사용
      }

      // Registered Office Address
      const registeredAddress = profileData.registeredAddress || {};
      const addressInfo = {
        ADDRESS_TYPE_CD: 'B07001', // Local Address (일반적으로 Registered Address는 Local)
        POSTAL_CODE: registeredAddress.postalCode || '',
        BLOCK_NO: registeredAddress.blockHouseNo || registeredAddress.blockNo || '',
        STREET_NAME: registeredAddress.streetName || '',
        BUILDING_NAME: registeredAddress.buildingEstateName || registeredAddress.buildingName || '',
        ADDR_LEVEL: registeredAddress.level || '',
        ADDR_UNIT: registeredAddress.unit || '',
        FOREIGN_ADDRESS_LINE_1: '',
        FOREIGN_ADDRESS_LINE_2: ''
      };
      const companyAddress = formatAddress(addressInfo, { useComma: true });
      if (companyAddress && typeof companyAddress === 'string' && companyAddress.trim()) {
        entityData.companyAddress = companyAddress;
      }

      // FINANCIAL_YEAR_END (날짜 형식)
      const financialYearEnd = profileData.accountingDates?.financialYearEnd || '';
      if (financialYearEnd) {
        entityData.financialYearEnd = financialYearEnd; // 날짜 형식 그대로 사용
      }

      // PRIMARY_ACTIVITY (SSIC 코드)
      const primaryActivityCode = profileData.registeredAddress?.primaryActivityCode || 
                                  profileData.primaryActivityCode || '';
      if (primaryActivityCode) {
        entityData.primaryActivityCode = primaryActivityCode;
        // L_SSIC에서 SSIC_TITLE 조회
        const primaryActivityDescription = getSSICTitle(primaryActivityCode);
        if (primaryActivityDescription) {
          entityData.primaryActivityDescription = primaryActivityDescription;
        }
      }

      // SECONDARY_ACTIVITY (SSIC 코드)
      const secondaryActivityCode = profileData.registeredAddress?.secondaryActivityCode || 
                                    profileData.secondaryActivityCode || '';
      if (secondaryActivityCode) {
        entityData.secondaryActivityCode = secondaryActivityCode;
        // L_SSIC에서 SSIC_TITLE 조회
        const secondaryActivityDescription = getSSICTitle(secondaryActivityCode);
        if (secondaryActivityDescription) {
          entityData.secondaryActivityDescription = secondaryActivityDescription;
        }
      }

      // JURISDICTION (L_BASECODE에서 CODE_NAME 조회)
      // L_ENTITY의 JURISDICTION 컬럼 값(TOTAL_CODE)을 사용하고, countryCode는 'CM' 고정
      // 백엔드에서 TOTAL_CODE를 jurisdictionCode로 보내줌
      const jurisdictionTotalCode = (profileData.registrySync as any)?.jurisdictionCode || 
                                    (profileData as any).jurisdictionCode || '';
      
      if (jurisdictionTotalCode) {
        try {
          // L_BASECODE에서 CODE_NAME 조회
          // countryCode는 'CM' 고정
          const countryCode = 'CM';
          const response = await baseCodeApi.getCodeByTotalCode(countryCode, jurisdictionTotalCode);
          const jurisdictionCode = (response as any).code || (response as any).data?.code;
          if (jurisdictionCode && jurisdictionCode.codeName) {
            entityData.entityJurisdiction = jurisdictionCode.codeName;
            console.log('✅ [useEntityDocVariables] Jurisdiction CODE_NAME 조회 성공:', {
              countryCode,
              totalCode: jurisdictionTotalCode,
              codeName: jurisdictionCode.codeName
            });
          } else {
            // CODE_NAME을 찾을 수 없으면 fallback으로 기존 값 사용
            console.warn('⚠️ [useEntityDocVariables] Jurisdiction CODE_NAME을 찾을 수 없음:', {
              countryCode,
              totalCode: jurisdictionTotalCode,
              response: jurisdictionCode
            });
            entityData.entityJurisdiction = profileData.registrySync?.jurisdiction || '';
          }
        } catch (error) {
          console.warn('⚠️ [useEntityDocVariables] Jurisdiction CODE_NAME 조회 실패:', error);
          // 에러 발생 시 fallback으로 기존 값 사용
          entityData.entityJurisdiction = profileData.registrySync?.jurisdiction || '';
        }
      } else {
        // TOTAL_CODE가 없으면 기존 값 사용 (이미 CODE_NAME일 수 있음)
        entityData.entityJurisdiction = profileData.registrySync?.jurisdiction || '';
      }

      // COMPANY_TYPE
      entityData.entityType = profileData.registrySync?.entityType || '';

      // Entity Email (TODO: 추후 확인 필요)
      entityData.companyEmail = '';

      console.log('✅ [useEntityDocVariables] Entity 데이터 조회 완료:', {
        incorporationDate: entityData.incorporationDate,
        financialYearEnd: entityData.financialYearEnd,
        entityJurisdiction: entityData.entityJurisdiction,
        fullData: entityData
      });
      return entityData;
    } catch (error) {
      console.error('❌ [useEntityDocVariables] Entity 데이터 조회 실패:', error);
      return {};
    }
  };

  return {
    getEntityData,
    loadSSICCache,
    getSSICTitle // SSIC_TITLE 조회 함수도 export
  };
};
